import { FC, useState, JSX } from "react";
import {
  AssociatedGeoPoint,
  GroupInfoData,
  GroupMembership,
  UserData,
} from "../../../../redux/actions/users/userTypes";

import { useTranslation } from "react-i18next";
import {
  isRoleManager,
  UserGroup,
  UserGroupMembership,
  UserRole,
} from "../../../../utils/auto-grouping";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import { getLocalizedTextByLocaleVersionTwo } from "../../../../utils/localized-text";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  ExtendedLicense,
  LicenseStatus,
} from "../../../../redux/actions/licenses/licenceTypes";
import { renderLicenseCredits } from "../../../licenses/LicenceCredits";
import { userSBFilterValues } from "../../../../redux/slices/userGroups/userGroupsSliceMultipleTables";

type UserActualInformationParams = {
  selectedUser: UserData | null;
  createdAtModify: string;
  getUserRoleFromGroups(
    groups?: UserGroup[],
    memberships?: UserGroupMembership[],
    roles?: UserRole[],
  ): string;
  memberships: GroupMembership[] | undefined;
  userRoles: any;
  groups: GroupInfoData[] | undefined;
  locale: string;
  showPassword: boolean;
  license: ExtendedLicense | undefined;
  decipherPassword: string | null;
  loadingUserPassword: boolean;
  handleClickShowPassword: () => Promise<void>;
  unlinkLicenseUser: (id: string) => void;
  selectUser: () => void;
  availLicencesManagement: () => JSX.Element;
  setShowPassword: (value: boolean) => void;
  setIsEditingUser: (userEditValues: any) => void;
  userValuesUpdate: {
    role: string;
    site: number;
    sites: number[];
    actions: {
      allValuesSelected: boolean;
      valuesSelected: userSBFilterValues[];
    };
    copyOfActions: {
      valuesSelected: userSBFilterValues[];
    };
    contactEmail: string;
    phone: string;
    phoneCode: string;
  };
  selectLocalePhoneDefaultInfo: () => {
    code: string;
    label: string;
    phone: string;
    suggested: boolean;
  };
  selectLocalePhoneDefaultCode: () => "+33" | "+1" | "+52" | "+49";
};

const UserActualInformation: FC<UserActualInformationParams> = ({
  selectedUser,
  createdAtModify,
  getUserRoleFromGroups,
  memberships,
  groups,
  userRoles,
  locale,
  showPassword,
  license,
  decipherPassword,
  loadingUserPassword,
  handleClickShowPassword,
  unlinkLicenseUser,
  selectUser,
  availLicencesManagement,
  setShowPassword,
  setIsEditingUser,
  userValuesUpdate,
  selectLocalePhoneDefaultInfo,
  selectLocalePhoneDefaultCode,
}) => {
  const [t] = useTranslation("global");
  const [showMoreActions, setShowMore] = useState(false);
  const [showMoreLocations, setShowMoreLocations] = useState(false);

  const assignedActions = (selectedUser as any)?.assignedAction?.map(
    (x: any) => {
      return `(${getLocalizedTextByLocaleVersionTwo(
        locale,
        x.cerealType.localizedTexts,
      )}) ${getLocalizedTextByLocaleVersionTwo(locale, x.name.localizedTexts)}`;
    },
  );

  const assignedSites = (selectedUser as any)?.assignedSite?.map(
    (x: AssociatedGeoPoint) => {
      return x;
    },
  );

  const showActions = assignedActions?.slice(
    0,
    !showMoreActions && assignedActions.length > 3 ? 3 : assignedActions.length,
  );

  const showLocations = assignedSites?.slice(
    0,
    !showMoreLocations && assignedSites.length > 3 ? 3 : assignedSites.length,
  );

  const isManager = isRoleManager(groups, memberships, userRoles);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <div className="modal-detail-user-container">
      <div>
        <div className="username-title-modal">{selectedUser?.username}</div>
        <div className="username-subtitle-modal">
          {`${t("Users_details.created")} ${
            (selectedUser as any)?.creator === ""
              ? t("Users_details.at")
              : t("Users_details.by")
          } ${createdAtModify}`}
        </div>
      </div>
      <div className="access-and-permissions">
        <div className="access-title">{t("Users_details.access_perm")}</div>
        <div className="access-perm-info">
          <div className="access-values-users">
            <div className="option-left mt-2">
              <span className="title-view-span">{t("Users_details.role")}</span>
            </div>
            <div className="option-right">
              <Chip
                size="small"
                label={getUserRoleFromGroups(groups, memberships, userRoles)}
                variant="outlined"
              />
            </div>
          </div>
          <div className="access-values-users">
            <div className="option-left">
              <span className="title-view-span">
                {t("Users_details.assigned_site")}
              </span>
            </div>
            <div className="option-right-list">
              {assignedSites?.length >= 1 ? (
                showLocations?.map((actions: any) => {
                  return <li className="list-value-user">{actions?.name}</li>;
                })
              ) : (
                <li className="list-value-user">
                  {`${assignedSites?.length} ${t(
                    "Users_tables.columns_table_one.actions",
                  )}`}
                </li>
              )}
              {assignedSites && assignedSites?.length > 3 ? (
                <Button
                  sx={{ textTransform: "none" }}
                  onClick={() =>
                    setShowMoreLocations(showMoreLocations ? false : true)
                  }
                  endIcon={
                    !showMoreLocations ? <ExpandMoreIcon /> : <ExpandLessIcon />
                  }
                >
                  {!showMoreLocations
                    ? `+ ${assignedSites?.length - 3} ${t(
                        "Common_options.others",
                      )} `
                    : t("Common_options.show_less")}
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>

          <div className="access-values-users last-value-user-details">
            <div className="option-left">
              <span className="title-view-span">
                {t("Users_details.assigned_action")}
              </span>
            </div>
            <div className="option-right-list">
              {assignedActions?.length >= 1 ? (
                showActions?.map((actions: any) => {
                  return <li className="list-value-user">{actions}</li>;
                })
              ) : (
                <li className="list-value-user">
                  {`${assignedActions?.length} ${t(
                    "Users_tables.columns_table_one.actions",
                  )}`}
                </li>
              )}
              {assignedActions && assignedActions?.length > 3 ? (
                <Button
                  sx={{ textTransform: "none" }}
                  onClick={() => setShowMore(showMoreActions ? false : true)}
                  endIcon={
                    !showMoreActions ? <ExpandMoreIcon /> : <ExpandLessIcon />
                  }
                >
                  {!showMoreActions
                    ? `+ ${assignedActions?.length - 3} ${t(
                        "Common_options.others",
                      )} `
                    : t("Common_options.show_less")}
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
        <div className="divider-user-details" />
        <div className="access-title">{t("Users_details.login_info")}</div>

        {!isManager ? (
          <>
            <div className="access-values-users">
              <div className="option-left">
                <span className="title-view-span">
                  {t("Users_details.pass")}
                </span>
              </div>
            </div>
            <div className="option-password">
              <OutlinedInput
                id="outlined-basic"
                disabled={true}
                value={!showPassword ? "**************" : decipherPassword}
                type={showPassword ? "text" : "password"}
                style={{ width: "100%", backgroundColor: "white" }}
                endAdornment={
                  <InputAdornment position="end">
                    {!loadingUserPassword ? (
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ) : (
                      <Box sx={{ display: "flex" }}>
                        <CircularProgress />
                      </Box>
                    )}
                  </InputAdornment>
                }
              />
            </div>
          </>
        ) : (
          ""
        )}

        <div className="access-values-users last-value-user-details">
          <div className="option-left">
            <span className="title-view-span">{t("Users_details.email")}</span>
          </div>
          <div className="option-right-list">
            <li className="list-value-user">
              {selectedUser?.email === ""
                ? t("Common_options.null")
                : selectedUser?.email}
            </li>
          </div>
        </div>

        <div className="divider-user-details" />
        <div className="access-title">{t("licenses.License")}</div>
        {license ? (
          <>
            <div className="access-values-users">
              <div className="option-left mt-2">
                <span className="title-view-span">
                  {t("Users_tables.columns_table_one.balance")}
                </span>
              </div>
              <div className="option-right">
                {license ? renderLicenseCredits([license]) : null}
              </div>
            </div>
            <div className="access-values-users">
              <div className="option-left">
                <span className="title-view-span">
                  {t("licenses.expiration")}
                </span>
              </div>
              <div className="option-right">
                {new Date(license.expiryDate).toLocaleDateString()}
              </div>
            </div>
            <div className="access-values-users last-value-user-details">
              <div className="option-right-list">
                {license.statusConstantId > LicenseStatus.assigned ? null : (
                  <Button
                    color="error"
                    onClick={() => {
                      unlinkLicenseUser(license.id);
                      selectUser();
                      setTimeout(() => window.location.reload(), 100); // yes, we're really doing this
                    }}
                  >
                    {t("Common_options.Remove")}
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          availLicencesManagement()
        )}
      </div>

      <div className="buttons-user-view">
        <div className="container-buttons-users-actions">
          <div>
            <Button
              onClick={() =>
                setIsEditingUser({
                  ...userValuesUpdate,
                  role: (selectedUser as any)?.role,
                  site: (selectedUser as any)?.assignedSiteId[0], /// <= Modify just site-user
                  phoneCode: selectLocalePhoneDefaultInfo(),
                  phone: selectLocalePhoneDefaultCode(),
                  copyOfActions: {
                    valuesSelected: (selectedUser as any)?.assignedAction?.map(
                      (c: any) => ({
                        id: c.id,
                        name: getLocalizedTextByLocaleVersionTwo(
                          locale,
                          c.name.localizedTexts,
                        ),
                        scenarioId: c.scenarioId,
                        scopeId: c.scopeId,
                      }),
                    ),
                  },
                  contactEmail: selectedUser?.email,
                  actions: {
                    allValuesSelected: false,
                    valuesSelected: (selectedUser as any)?.assignedAction?.map(
                      (c: any) => ({
                        id: c.id,
                        name: getLocalizedTextByLocaleVersionTwo(
                          locale,
                          c.name.localizedTexts,
                        ),
                        scenarioId: c.scenarioId,
                        scopeId: c.scopeId,
                      }),
                    ),
                  },
                })
              }
              endIcon={<EditIcon />}
              sx={{ textTransform: "none" }}
            >
              {t("Common_options.Edit")}
            </Button>
          </div>
          <div>
            <Button
              variant="contained"
              onClick={() => {
                selectUser();
                setShowPassword(false);
              }}
              sx={{ textTransform: "none" }}
            >
              {t("Fleet_overview.search_sidebar.close")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserActualInformation;
